/*
  SHUDHI trust marquee.

  The section renders one group of items. This element clones that group until
  the track is at least twice the width of the viewport, then animates the
  track by exactly one group width, so the loop is seamless at any screen size
  and with any number of items.

  Only `transform` is animated. The animation is paused whenever the band is
  offscreen, and never starts at all if the visitor prefers reduced motion.
*/
if (!customElements.get('shudhi-marquee')) {
  customElements.define(
    'shudhi-marquee',
    class ShudhiMarquee extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-marquee-track]');
        this.group = this.querySelector('[data-marquee-group]');
        if (!this.track || !this.group) return;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.resizeTimer = null;

        this.onResize = this.onResize.bind(this);
        this.onMotionChange = this.onMotionChange.bind(this);

        this.build();

        window.addEventListener('resize', this.onResize, { passive: true });
        this.reduceMotion.addEventListener('change', this.onMotionChange);

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.track.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
          });
        });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        this.reduceMotion?.removeEventListener('change', this.onMotionChange);
        this.observer?.disconnect();
        clearTimeout(this.resizeTimer);
      }

      build() {
        this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());

        if (this.reduceMotion.matches) {
          this.track.style.removeProperty('--marquee-distance');
          this.track.style.removeProperty('--marquee-duration');
          return;
        }

        const groupWidth = this.group.offsetWidth;
        if (!groupWidth) return;

        // One copy for the seamless wrap, plus as many as the viewport needs.
        const copies = Math.max(1, Math.ceil((window.innerWidth * 2) / groupWidth));
        const fragment = document.createDocumentFragment();

        for (let i = 0; i < copies; i += 1) {
          const clone = this.group.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-marquee-clone', '');
          clone.removeAttribute('data-marquee-group');
          fragment.appendChild(clone);
        }

        this.track.appendChild(fragment);

        // Speed is fixed in pixels per second so short and long bands read the same.
        const speed = Number(this.dataset.speed) || 60;
        this.track.style.setProperty('--marquee-distance', `-${groupWidth}px`);
        this.track.style.setProperty('--marquee-duration', `${groupWidth / speed}s`);
      }

      onResize() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.build(), 200);
      }

      onMotionChange() {
        this.build();
      }
    }
  );
}
